import { useCallback, useEffect, useState } from 'react'
import { ExternalLink, RefreshCw, CheckCircle2, XCircle, Package, Download } from 'lucide-react'
import * as XLSX from 'xlsx'
import { api } from '../../../../lib/api'
import { useLanguage } from '../../../../context/LanguageContext'

const PAGE_SIZE = 20
const EXPORT_LIMIT = 10000

const STATUS_LABEL = {
  received: 'Recibido',
  validated: 'Validado',
  cancelled: 'Anulado',
}

/**
 * Panel de gestión de recepciones HU (tab Sala Mezcla en Lotes y Trazabilidad).
 */
export default function SalaMezclaGestion({ factory }) {
  const { t } = useLanguage()
  const sm = t.produccion?.lotesTrazabilidad || {}
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')
  const [huFilter, setHuFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const factoryCode = (factory && factory !== 'CX') ? factory : ''

  const buildParams = useCallback((p, { forExport = false } = {}) => {
    const params = new URLSearchParams()
    if (factoryCode) params.set('factory', factoryCode)
    if (huFilter.trim()) params.set('hu_sscc', huFilter.trim())
    if (statusFilter) params.set('status', statusFilter)
    if (dateFrom) params.set('from', dateFrom)
    if (dateTo) params.set('to', dateTo)
    params.set('limit', String(forExport ? EXPORT_LIMIT : PAGE_SIZE))
    params.set('offset', String(forExport ? 0 : (p - 1) * PAGE_SIZE))
    return params
  }, [factoryCode, huFilter, statusFilter, dateFrom, dateTo])

  const load = useCallback(async (p = page) => {
    setLoading(true)
    setError('')
    try {
      const res = await api.get(`/api/mixing-room/hu-receptions?${buildParams(p)}`)
      setRows(res?.data || [])
      setTotal(res?.total || 0)
    } catch (err) {
      setError(err?.message || String(err))
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [buildParams, page])

  useEffect(() => {
    load(1)
    setPage(1)
  }, [factoryCode]) // eslint-disable-line react-hooks/exhaustive-deps

  const patchStatus = async (id, status) => {
    try {
      await api.patch(`/api/mixing-room/hu-receptions/${id}`, { status })
      await load(page)
    } catch (err) {
      setError(err?.message || String(err))
    }
  }

  const exportExcel = async () => {
    setExporting(true)
    setError('')
    try {
      const res = await api.get(`/api/mixing-room/hu-receptions?${buildParams(1, { forExport: true })}`)
      const data = res?.data || []
      if (data.length === 0) {
        setError('No hay datos para exportar con los filtros actuales')
        return
      }

      const sheetRows = data.map((r) => ({
        HU: r.hu_sscc,
        Fábrica: r.factory,
        'Entrada manual': r.manual_entry ? 'Sí' : 'No',
        Operador: r.received_by_name || '',
        Email: r.received_by_email || '',
        Fecha: r.received_at ? new Date(r.received_at).toLocaleString() : '',
        Estado: STATUS_LABEL[r.status] || r.status,
        'Validado por': r.validated_by_email || '',
        'Fecha validación': r.validated_at ? new Date(r.validated_at).toLocaleString() : '',
        Notas: r.notes || '',
      }))

      const ws = XLSX.utils.json_to_sheet(sheetRows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sala Mezcla')
      const stamp = new Date().toISOString().slice(0, 10)
      const factoryPart = factoryCode || 'ALL'
      XLSX.writeFile(wb, `sala-mezcla_${factoryPart}_${stamp}.xlsx`)
    } catch (err) {
      setError(err?.message || String(err))
    } finally {
      setExporting(false)
    }
  }

  const pdaUrl = factoryCode
    ? `${window.location.origin}/${factoryCode}/pda/sala-mezcla`
    : `${window.location.origin}/pda/sala-mezcla`

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-400" />
            {sm.salaMezclaTitle || 'Control Sala Mezcla'}
          </h2>
          <p className="text-sm text-gray-400">
            {(sm.salaMezclaDesc || 'Recepción de pallets (HU / SSCC) desde PDA')}
            {factoryCode ? ` · ${factoryCode}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={exportExcel}
            disabled={exporting || loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-sm text-white"
          >
            <Download size={16} className={exporting ? 'animate-pulse' : ''} />
            {exporting ? 'Exportando…' : 'Exportar Excel'}
          </button>
          <a
            href={pdaUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm text-white"
          >
            <ExternalLink size={16} />
            {sm.openPda || 'Abrir PDA'}
          </a>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(pdaUrl)
            }}
            className="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm text-gray-200"
          >
            {sm.copyPdaLink || 'Copiar link PDA'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-400 mb-1">HU / SSCC</label>
          <input
            value={huFilter}
            onChange={(e) => setHuFilter(e.target.value)}
            className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white text-sm font-mono"
            placeholder="1000966737"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Estado</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white text-sm"
          >
            <option value="">Todos</option>
            <option value="received">Recibido</option>
            <option value="validated">Validado</option>
            <option value="cancelled">Anulado</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Desde</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Hasta</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => { setPage(1); load(1) }}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm text-white"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Buscar
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-800 text-gray-300">
            <tr>
              <th className="px-3 py-2 text-left">HU</th>
              <th className="px-3 py-2 text-left">Fábrica</th>
              <th className="px-3 py-2 text-left">Operador</th>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">Cargando…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">Sin recepciones</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-700 hover:bg-gray-800/40">
                <td className="px-3 py-2 font-mono text-white">
                  {r.hu_sscc}
                  {r.manual_entry && <span className="ml-2 text-xs text-amber-400">manual</span>}
                </td>
                <td className="px-3 py-2 text-gray-300">{r.factory}</td>
                <td className="px-3 py-2 text-gray-300">
                  <div>{r.received_by_name || '—'}</div>
                  <div className="text-xs text-gray-500">{r.received_by_email}</div>
                </td>
                <td className="px-3 py-2 text-gray-300">
                  {r.received_at ? new Date(r.received_at).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={r.status} />
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {r.status === 'received' && (
                      <button
                        type="button"
                        onClick={() => patchStatus(r.id, 'validated')}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-600/20 border border-green-500/40 text-green-300 hover:bg-green-600/30 text-xs"
                        title="Validar"
                      >
                        <CheckCircle2 size={14} />
                        Validar
                      </button>
                    )}
                    {(r.status === 'received' || r.status === 'validated') && (
                      <button
                        type="button"
                        onClick={() => patchStatus(r.id, 'cancelled')}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-600/20 border border-red-500/40 text-red-300 hover:bg-red-600/30 text-xs"
                        title="Anular recepción"
                      >
                        <XCircle size={14} />
                        Anular
                      </button>
                    )}
                    {r.status === 'cancelled' && (
                      <button
                        type="button"
                        onClick={() => patchStatus(r.id, 'received')}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-600/20 border border-blue-500/40 text-blue-300 hover:bg-blue-600/30 text-xs"
                        title="Reactivar como recibido"
                      >
                        <RefreshCw size={14} />
                        Reactivar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(total > 0 || total > PAGE_SIZE) && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>{total} registros</span>
          {total > PAGE_SIZE && (
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => { const p = page - 1; setPage(p); load(p) }}
                className="px-3 py-1 rounded bg-gray-800 disabled:opacity-40"
              >
                Anterior
              </button>
              <span>{page} / {totalPages}</span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => { const p = page + 1; setPage(p); load(p) }}
                className="px-3 py-1 rounded bg-gray-800 disabled:opacity-40"
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status }) {
  const map = {
    received: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
    validated: 'bg-green-500/20 text-green-300 border-green-500/40',
    cancelled: 'bg-red-500/20 text-red-300 border-red-500/40',
  }
  const label = STATUS_LABEL
  return (
    <span className={`px-2 py-0.5 rounded border text-xs ${map[status] || 'bg-gray-700 text-gray-300'}`}>
      {label[status] || status}
    </span>
  )
}
